import { createSelector } from '@opengov/compose-app/reselect';
import { selectFeatureFlags } from '@opengov/feature-flag/launchDarkly';
import {
  FLAG_ADD_PROPOSALS_TO_STORIES,
  FLAG_BNP_NEW_REPORT_TILE_MODAL,
  FLAG_PRODUCT_REPORTING_CHART_AND_DETAILS_V3_ENABLED,
  FLAG_PRODUCT_REPORTING_FEATURE_ACCOUNT_SUMMARY_CHARTS_IN_EMBEDS,
  FLAG_PRODUCT_STORIES_PUBLICATIONS_GOOGLE_TRANSLATOR,
  FLAG_STORIES_AI_STORY_CONTENT_GENERATION,
  FLAG_STORIES_BULK_UPDATE_STATUS,
  FLAG_STORIES_REMOVE_JS_EMBEDS,
  FLAG_STORIES_SUMMARIZER_GA,
  FLAG_STORIES_OG_ASSIST_GENERATE_SUMMARY,
  FLAG_PRODUCT_REPORTING_FEATURE_CHARTS_CUSTOMIZATION_TITLE_AND_DESCRIPTION
} from '@opengov/stories-shared/constants';

export const selectStoriesPublicationFlag = createSelector(selectFeatureFlags, (featureFlags) => true);

export const selectStoriesSubscriptionFlag = createSelector(selectFeatureFlags, (featureFlags) =>
  featureFlags.get('product-budget-feature-story-subscription')
);

export const selectStoriesProgressTrackerFlag = createSelector(selectFeatureFlags, (featureFlags) =>
  featureFlags.get('product-budget-feature-story-progress-tracker')
);

export const selectStoriesUpdateReportEmbedsFeatureFlag = createSelector(selectFeatureFlags, (featureFlags) =>
  featureFlags.get('product-stories-update-report-embeds')
);

export const selectStoriesV3EmbedsFlag = createSelector(selectFeatureFlags, (flags) =>
  flags.get('product-feature-reporting-embed-v3')
);

export const selectStoriesEmbedV2EditingEnabledFlag = createSelector(selectFeatureFlags, (flags) =>
  flags.get('product-feature-reporting-embed-v2-editing-enabled')
);

export const selectAddPropsalsToStoriesFlag: (flags) => boolean = createSelector(selectFeatureFlags, (flags) => {
  return flags.get(FLAG_ADD_PROPOSALS_TO_STORIES);
});

export const selectStoriesBulkUpdateStoryStatusFeatureFlag = createSelector(selectFeatureFlags, (flags) =>
  flags.get(FLAG_STORIES_BULK_UPDATE_STATUS)
);

export const selectStoriesRemoveJsEmbedsFeatureFlag = createSelector(selectFeatureFlags, (flags) =>
  flags.get(FLAG_STORIES_REMOVE_JS_EMBEDS)
);
export const selectUseAiToGenerateContentFlag = createSelector(selectFeatureFlags, (flags) =>
  flags.get(FLAG_STORIES_AI_STORY_CONTENT_GENERATION)
);

export const selectStoriesSummarizerGAFlag = createSelector(selectFeatureFlags, (flags) =>
  flags.get(FLAG_STORIES_SUMMARIZER_GA)
);

/** Routes "Generate Summary" to OG Assist; off, or absent, keeps the existing summary modal. */
export const selectStoriesOgAssistGenerateSummaryFlag = createSelector(selectFeatureFlags, (flags) =>
  flags.get(FLAG_STORIES_OG_ASSIST_GENERATE_SUMMARY)
);

export const selectOnlinePublicationTranslatorFlag = createSelector(selectFeatureFlags, (featureFlags) =>
  featureFlags.get(FLAG_PRODUCT_STORIES_PUBLICATIONS_GOOGLE_TRANSLATOR)
);

export const selectAccountSummaryChartsInEmbedFlag = createSelector(selectFeatureFlags, (featureFlags) =>
  featureFlags.get(FLAG_PRODUCT_REPORTING_FEATURE_ACCOUNT_SUMMARY_CHARTS_IN_EMBEDS)
);

export const selectIsAiToggleFeatureEnabled = createSelector(selectFeatureFlags, (flags) =>
  flags.get('bnp-ai-toggle-admin-setting')
);

export const selectIsCustomSpreadsheetReportEnabledFlag = createSelector(selectFeatureFlags, (flags) =>
  flags.get('bnp-acfr-reports-stories')
);

export const selectUseV3EmbedsForChartAndDetailsReportsFlag = createSelector(selectFeatureFlags, (flags) =>
  flags.get(FLAG_PRODUCT_REPORTING_CHART_AND_DETAILS_V3_ENABLED)
);

export const selectIsNewReportTileModalEnabledFlag = createSelector(selectFeatureFlags, (flags) =>
  flags.get(FLAG_BNP_NEW_REPORT_TILE_MODAL)
);

export const selectIsFormAndWorkflowsFeatureFlagEnabled = createSelector(
  selectFeatureFlags,
  (flags) => flags.get('product-budget-feature-forms-and-workflows') as boolean
);

export const selectIsPerformanceManagementFeatureFlagEnabled = createSelector(
  selectFeatureFlags,
  (flags) => flags.get('product-budget-performance-management') as boolean
);

export const selectChartTitleAndDescriptionInEmbedFlag = createSelector(selectFeatureFlags, (flags) =>
  flags.get(FLAG_PRODUCT_REPORTING_FEATURE_CHARTS_CUSTOMIZATION_TITLE_AND_DESCRIPTION)
);

// Story-list / folder feature flags (migrated from storyListSelectors)
export const selectStoriesOrganizationFlag = createSelector(selectFeatureFlags, (featureFlags) =>
  featureFlags.get('product-stories-organization-foldering')
);

export const selectCopyStoriesFoldersFlag = createSelector(selectFeatureFlags, (featureFlags) =>
  featureFlags.get('product-stories-copy-stories-folders')
);

export const selectCopyIndividualStoriesFlag = createSelector(selectFeatureFlags, (featureFlags) =>
  featureFlags.get('product-stories-copy-individual-stories')
);

export const selectStoriesExportFolderPdfFlag = createSelector(selectFeatureFlags, (featureFlags) =>
  featureFlags.get('product-stories-export-folder-pdf')
);

export const selectStoryMarginsEnabled = createSelector(selectFeatureFlags, (featureFlags) =>
  featureFlags.get('product-stories-feature-story-margins')
);

export const selectShowGlobalNavBar = createSelector(selectFeatureFlags, (featureFlags) =>
  featureFlags.get('product-budget-feature-global-nav-bar')
);

export const selectShowFollowableStories = createSelector(selectFeatureFlags, (featureFlags) =>
  featureFlags.get('product-budget-feature-story-subscription')
);
